// Which rooms and workplaces the reservation board shows, and the filter it was
// left with.
//
// Two things went wrong (Camiel, 2026-09-17). Opening Reserveren always showed
// every room — on a board that colours by status and not by room, so a full
// board tells the rooms apart by nothing. And clicking them off one by one
// turned them all back ON at the last one: an empty selection wrote an empty
// value to the URL, and an empty value meant "all".
//
// In the URL `resourceIds` is absent for all, "none" for none, and the ids
// otherwise. The last filter is kept per browser, like the mail screen's tab.

import { RESERVATION_BRANCHES } from "./labels";

export const NO_RESOURCES = "none";

export function selectedResourceIds(param: string | undefined, allIds: readonly string[]): Set<string> {
  if (!param) return new Set(allIds);
  if (param === NO_RESOURCES) return new Set();
  return new Set(param.split(",").filter(Boolean));
}

export function resourceIdsParam(selected: ReadonlySet<string>, allIds: readonly string[]): string {
  if (selected.size === 0) return NO_RESOURCES;
  if (allIds.length > 0 && allIds.every((id) => selected.has(id))) return "";
  return [...selected].join(",");
}

// "Alles": every one of the shown resources on, or — when they already all are —
// every one of them off. Resources hidden by the vestiging or type filter keep
// whatever they had.
export function toggleAllShown(
  selected: ReadonlySet<string>,
  shownIds: readonly string[],
): Set<string> {
  const next = new Set(selected);
  const allOn = shownIds.length > 0 && shownIds.every((id) => next.has(id));
  for (const id of shownIds) {
    if (allOn) next.delete(id);
    else next.add(id);
  }
  return next;
}

export interface ReservationFilterPrefs {
  resourceIds: string; // "" all · "none" · comma-separated ids
  branch: string; // "" all, or one of RESERVATION_BRANCHES
  type: string; // "" all · "room" · "workplace"
}

const KEY = "wlx:reservationsFilter";

export function isDefaultFilter(p: ReservationFilterPrefs): boolean {
  return !p.resourceIds && !p.branch && !p.type;
}

// Anything unreadable is dropped rather than applied: a stored branch that no
// longer exists would otherwise hide every room without a word.
export function parseReservationFilterPrefs(raw: string | null): ReservationFilterPrefs | null {
  if (!raw) return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  const resourceIds =
    typeof d["resourceIds"] === "string" && d["resourceIds"].length <= 4000 && /^[\w,-]*$/.test(d["resourceIds"])
      ? d["resourceIds"]
      : "";
  const branch =
    typeof d["branch"] === "string" && (RESERVATION_BRANCHES as readonly string[]).includes(d["branch"])
      ? d["branch"]
      : "";
  const type = d["type"] === "room" || d["type"] === "workplace" ? d["type"] : "";
  return { resourceIds, branch, type };
}

export function readReservationFilterPrefs(): ReservationFilterPrefs | null {
  try {
    return parseReservationFilterPrefs(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

export function writeReservationFilterPrefs(prefs: ReservationFilterPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* nothing to do — the screen keeps the filter, the next visit starts fresh */
  }
}
